/**
 * drawerParser.js — URL segment parser for the d.<component>.<args> grammar (XOL-209 PR 2).
 *
 * URL grammar:  /basepath/d.<name>.<arg1>.<arg2>.<arg3>/d.<name>...
 *
 * Exports:
 *   parseDrawerPath(tail)        — string → Array<{name, args}>
 *   serialiseDrawerStack(stack)  — Array<{name, args}> → string
 *   canonicalisePath(tail)       — round-trip parse+serialise (used by migration shim)
 *   findInStack(stack, name)     — returns the matching entry or null
 *   hasDrawer(stack, name)       — boolean convenience wrapper for findInStack
 */

import { drawerSchema, isKnownDrawer } from '@/lib/drawerSchema'


// Drawer name allows camelCase (e.g. `siteUtil`) — registry keys are
// JavaScript identifiers, not URL slugs. Accept [a-zA-Z][a-zA-Z0-9]*.
const SEGMENT_RE = /^d\.[a-zA-Z][a-zA-Z0-9]*(\..*)?$/

/**
 * Parse a URL tail (everything after the basepath) into a stack of drawer entries.
 * Unknown component names are silently dropped.
 * id arg that is NaN after Number() coercion causes the entry to be dropped.
 * Extra positional args beyond the registry shape are ignored.
 *
 * @param {string} tail  e.g. "d.site.555.power/d.bill.789"
 * @returns {Array<{name:string, args:Object}>}
 */
export function parseDrawerPath (tail) {
  if (!tail) return []

  var segments = tail.split('/').filter(function (seg) {
    return SEGMENT_RE.test(seg)
  })

  var stack = []

  for (var i = 0; i < segments.length; i++) {
    var parts = segments[i].split('.')
    // parts[0] === 'd', parts[1] === name, parts[2..] === positional arg values
    var name = parts[1]

    if (!isKnownDrawer(name)) continue

    var shape = drawerSchema[name]
    var argNames = shape.args
    var types = shape.types
    var rawValues = parts.slice(2)

    var args = {}
    var valid = true

    for (var j = 0; j < argNames.length; j++) {
      var argName = argNames[j]
      var raw = rawValues[j]

      // Int-typed args (including id at index 0) must be present and numeric.
      // Treat missing/empty as invalid only for int args — string args allow
      // missing values (default to null for optional tab/subtab).
      if (types[argName] === 'optint') {
        // XOL-596: an optional numeric arg. Absent = null; present but not a number = the
        // whole entry is invalid, like an int.
        if (raw === undefined || raw === '') {
          args[argName] = null
        } else if (isNaN(Number(raw))) {
          valid = false
          break
        } else {
          args[argName] = Number(raw)
        }
      } else if (types[argName] === 'int') {
        if (raw === undefined || raw === '') {
          valid = false
          break
        }
        var num = Number(raw)
        if (isNaN(num)) {
          valid = false
          break
        }
        args[argName] = num
      } else {
        if (raw === undefined || raw === '') {
          args[argName] = null
        } else {
          args[argName] = raw
        }
      }
    }

    if (!valid) continue

    stack.push({ name: name, args: args })
  }

  return stack
}

/**
 * Serialise a drawer stack back to a URL tail string.
 * Trailing null/undefined args are trimmed: {id:5, tab:null, subtab:null} → "d.site.5"
 *
 * @param {Array<{name:string, args:Object}>} stack
 * @returns {string}
 */
export function serialiseDrawerStack (stack) {
  if (!stack || !stack.length) return ''

  return stack.map(function (entry) {
    var shape = drawerSchema[entry.name]
    if (!shape) return null

    var argNames = shape.args
    var parts = ['d', entry.name]

    // Collect arg values, then trim trailing nulls/undefineds
    var values = argNames.map(function (argName) {
      var val = entry.args[argName]
      return (val === null || val === undefined) ? null : String(val)
    })

    // Trim trailing nulls
    while (values.length > 0 && values[values.length - 1] === null) {
      values.pop()
    }

    return parts.concat(values).join('.')
  }).filter(Boolean).join('/')
}

/**
 * Round-trip: parse then re-serialise. Used by the router migration shim
 * to normalise incoming URL tails to canonical form.
 *
 * @param {string} tail
 * @returns {string}
 */
export function canonicalisePath (tail) {
  return serialiseDrawerStack(parseDrawerPath(tail))
}

/**
 * Find a named entry in a parsed stack. Returns the entry or null.
 *
 * @param {Array<{name:string, args:Object}>} stack
 * @param {string} name
 * @returns {{name:string, args:Object}|null}
 */
export function findInStack (stack, name) {
  for (var i = 0; i < stack.length; i++) {
    if (stack[i].name === name) return stack[i]
  }
  return null
}

/**
 * Boolean convenience: returns true if the named drawer is present in the stack.
 *
 * @param {Array<{name:string, args:Object}>} stack
 * @param {string} name
 * @returns {boolean}
 */
export function hasDrawer (stack, name) {
  return findInStack(stack, name) !== null
}
